import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users,
  UserPlus,
  Mail,
  Phone,
  Bell,
  Send,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  Search,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = ['Investor', 'Advisor', 'Co-Founder', 'Legal Counsel', 'Director', 'Other'];

const formatDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Toggle Switch — small green pill style matching #00E87A accent
// ─────────────────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testId}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-[#00E87A]' : 'bg-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Contact Form Modal — used for both Add and Edit
// ─────────────────────────────────────────────────────────────────────────────
const ContactFormModal = ({ initial, companyId, token, onClose, onSaved, mode }) => {
  const isEdit = mode === 'edit';
  const [name, setName] = useState(initial?.name || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [roleLabel, setRoleLabel] = useState(initial?.role_label || 'Investor');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [isVdrEnabled, setIsVdrEnabled] = useState(initial?.is_vdr_enabled || false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) return setError('Name is required');
    if (!isEdit && !email.trim()) return setError('Email is required');

    setSubmitting(true);
    try {
      if (isEdit) {
        // PATCH — never include email field
        const body = {
          name: name.trim(),
          phone: phone.trim() || null,
          role_label: roleLabel,
          notes: notes.trim() || null,
        };
        await axios.patch(
          `${API}/admin/companies/${companyId}/contacts/${initial.id}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(
          `${API}/admin/companies/${companyId}/contacts`,
          {
            name: name.trim(),
            email: email.trim().toLowerCase(),
            phone: phone.trim() || null,
            role_label: roleLabel,
            notes: notes.trim() || null,
            is_vdr_enabled: isVdrEnabled,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save party');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="contact-form-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5 text-[#00E87A]" /> : <UserPlus className="w-5 h-5 text-[#00E87A]" />}
            {isEdit ? 'Edit Party' : 'Add Party'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="contact-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
              data-testid="contact-name-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1.5">
              Email {!isEdit && <span className="text-red-400">*</span>}
              {isEdit && <span className="text-gray-500 text-xs ml-2">(cannot be changed)</span>}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isEdit}
              className={`w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="contact-email-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Phone <span className="text-gray-500 text-xs ml-1">(optional)</span></label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
              data-testid="contact-phone-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Role <span className="text-red-400">*</span></label>
            <select
              value={roleLabel}
              onChange={(e) => setRoleLabel(e.target.value)}
              className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
              data-testid="contact-role-select"
            >
              {ROLE_LABELS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Notes <span className="text-gray-500 text-xs ml-1">(admin only — not visible to contact)</span></label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-[#00E87A] focus:outline-none"
              data-testid="contact-notes-input"
            />
          </div>

          {!isEdit && (
            <div className="flex items-center justify-between bg-[#0D1117] border border-gray-700 rounded-lg px-3 py-3">
              <div>
                <p className="text-sm text-white font-medium">Enable Data Room Access</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  If on, an invite email is sent immediately on save.
                </p>
              </div>
              <Toggle checked={isVdrEnabled} onChange={setIsVdrEnabled} testId="contact-vdr-toggle" />
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white text-sm font-medium"
              data-testid="contact-form-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              data-testid="contact-form-save"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Add Party'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Delete Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
const DeleteConfirmModal = ({ contact, companyId, token, onClose, onDeleted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.delete(
        `${API}/admin/companies/${companyId}/contacts/${contact.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete party');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full" data-testid="contact-delete-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Remove Party?</h3>
        </div>
        <p className="text-gray-300 text-sm mb-5 leading-relaxed">
          Are you sure? This will remove <strong className="text-white">{contact.name}</strong> from
          all documents and revoke their access immediately.
        </p>
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-3 text-sm text-red-400">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-white text-sm font-medium"
            data-testid="contact-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={submitting}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="contact-delete-confirm"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Remove Party
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Party Detail Panel — read-only view of party + documents shared with them
// ─────────────────────────────────────────────────────────────────────────────
const formatDateTime = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

const STATUS_BADGE = {
  draft: { cls: 'bg-gray-500/15 text-gray-300 border-gray-500/30', label: 'Draft' },
  shared: { cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30', label: 'Shared' },
  final: { cls: 'bg-[#00E87A]/15 text-[#00E87A] border-[#00E87A]/30', label: 'Final' },
};

const PartyDetailPanel = ({ contact, companyId, token, onClose, onOpenDocument }) => {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/vdr/${companyId}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        const filtered = (r.data || []).filter(
          (d) => (d.access_contact_ids || []).includes(contact.id),
        );
        setDocs(filtered);
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.detail || 'Failed to load documents');
        setDocs([]);
      }
    })();
    return () => { active = false; };
  }, [companyId, contact.id, token]);

  const getApproval = (doc) => {
    const current = (doc.versions || []).find((v) => v.is_current);
    const approvals = current?.approvals || [];
    return approvals.find((a) => a.contact_id === contact.id);
  };

  const approvalDisplay = (doc) => {
    const a = getApproval(doc);
    if (!a) return { icon: Clock, cls: 'text-gray-400', text: 'Not shared on this version' };
    if (a.status === 'approved') {
      return { icon: CheckCircle2, cls: 'text-[#00E87A]', text: `Approved · ${formatDateTime(a.responded_at)}` };
    }
    if (a.status === 'changes_requested') {
      return { icon: RefreshCw, cls: 'text-orange-400', text: `Changes Requested · ${formatDateTime(a.responded_at)}` };
    }
    if (a.viewed_at) {
      return { icon: Clock, cls: 'text-amber-300', text: 'Pending — opened, not responded' };
    }
    return { icon: Clock, cls: 'text-amber-300', text: 'Pending — not yet opened' };
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="party-detail-panel">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="ml-auto relative w-full max-w-xl h-full bg-[#0D1117] border-l border-gray-700 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0D1117] border-b border-gray-700 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-white truncate">{contact.name}</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300">
                {contact.role_label}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-1 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> {contact.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 -mr-1"
            data-testid="party-detail-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Meta cards */}
        <div className="px-6 py-4 grid grid-cols-2 gap-3 border-b border-gray-800">
          <div className="bg-[#161B22] border border-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 uppercase tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> Data Room Access
            </div>
            <p className={`text-sm font-semibold mt-1 ${contact.is_vdr_enabled ? 'text-[#00E87A]' : 'text-gray-400'}`}>
              {contact.is_vdr_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <div className="bg-[#161B22] border border-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 uppercase tracking-wide">
              <Clock className="w-3.5 h-3.5" /> Last Login
            </div>
            <p className="text-sm font-semibold text-white mt-1">
              {contact.last_login ? formatDateTime(contact.last_login) : 'Never'}
            </p>
          </div>
        </div>

        {/* Documents */}
        <div className="px-6 py-5">
          <h4 className="text-sm font-semibold text-white mb-3">
            Documents Shared with {contact.name.split(' ')[0]}
          </h4>

          {docs === null && (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-sm text-red-400 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {docs && docs.length === 0 && !error && (
            <div className="bg-[#161B22] border border-gray-700 rounded-lg p-6 text-center" data-testid="party-detail-empty">
              <FileText className="w-8 h-8 text-gray-600 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-sm text-gray-300">No documents shared with {contact.name.split(' ')[0]} yet.</p>
              <p className="text-xs text-gray-500 mt-1">
                Upload a document and select {contact.name.split(' ')[0]} to share with them.
              </p>
            </div>
          )}

          {docs && docs.length > 0 && (
            <ul className="space-y-2" data-testid="party-detail-doc-list">
              {docs.map((d) => {
                const a = approvalDisplay(d);
                const badge = STATUS_BADGE[d.status] || STATUS_BADGE.draft;
                const Icon = a.icon;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onOpenDocument?.(d.id)}
                      className="w-full text-left bg-[#161B22] hover:bg-[#1c232e] border border-gray-700 hover:border-gray-600 rounded-lg p-3.5 transition-colors"
                      data-testid={`party-detail-doc-${d.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-md bg-gray-800/70 flex items-center justify-center shrink-0 mt-0.5">
                          <FileText className="w-4 h-4 text-gray-300" strokeWidth={1.6} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{d.title}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                Version {d.current_version_number}
                              </p>
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${badge.cls} shrink-0`}>
                              {badge.label}
                            </span>
                          </div>
                          <div className={`mt-2 inline-flex items-center gap-1.5 text-xs ${a.cls}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {a.text}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Contacts Tab
// ─────────────────────────────────────────────────────────────────────────────
const AdminContactsTab = ({ companyId, companyName, onOpenDocRoom }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [viewingContact, setViewingContact] = useState(null);
  const [rowBusy, setRowBusy] = useState({}); // contact_id -> 'toggle'|'reminder'|'resend'
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem('auth_token');

  const showToast = (text, kind = 'success') => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchContacts = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/companies/${companyId}/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(r.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load parties');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, [companyId]); // eslint-disable-line

  const handleToggleVdr = async (contact, next) => {
    setRowBusy({ ...rowBusy, [contact.id]: 'toggle' });
    try {
      await axios.patch(
        `${API}/admin/companies/${companyId}/contacts/${contact.id}`,
        { is_vdr_enabled: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showToast(next ? `Invite email sent to ${contact.name}` : `Access revoked for ${contact.name}`);
      await fetchContacts();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to toggle access', 'error');
    } finally {
      setRowBusy((prev) => { const n = { ...prev }; delete n[contact.id]; return n; });
    }
  };

  const handleResendInvite = async (contact) => {
    setRowBusy({ ...rowBusy, [contact.id]: 'resend' });
    try {
      await axios.post(
        `${API}/admin/companies/${companyId}/contacts/${contact.id}/resend-invite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showToast(`Invite resent to ${contact.name}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to resend invite', 'error');
    } finally {
      setRowBusy((prev) => { const n = { ...prev }; delete n[contact.id]; return n; });
    }
  };

  const handleSendReminder = async (contact) => {
    setRowBusy({ ...rowBusy, [contact.id]: 'reminder' });
    try {
      await axios.post(
        `${API}/admin/companies/${companyId}/contacts/${contact.id}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      showToast(`Reminder sent to ${contact.name}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to send reminder', 'error');
    } finally {
      setRowBusy((prev) => { const n = { ...prev }; delete n[contact.id]; return n; });
    }
  };

  const filtered = contacts.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q)
      || c.email?.toLowerCase().includes(q)
      || c.role_label?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-contacts-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#00E87A]" />
            Parties
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            Manage external parties (investors, advisors, counsel) for {companyName || 'this company'}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00E87A] hover:bg-[#00d96e] text-black rounded-lg text-sm font-semibold"
          data-testid="contacts-add-btn"
        >
          <UserPlus className="w-4 h-4" />
          Add Party
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search parties..."
          className="w-full bg-[#0D1117] border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-[#00E87A] focus:outline-none"
          data-testid="contacts-search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading parties...
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="contacts-error">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={fetchContacts} className="ml-auto text-xs text-red-300 hover:text-red-200 underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#161B22] border border-gray-700 rounded-xl py-16 text-center" data-testid="contacts-empty">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-300 font-medium">
            {search.trim() ? 'No parties match your search.' : 'No parties added yet.'}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {search.trim() ? 'Try a different search term.' : 'Add your first investor, advisor, or counsel.'}
          </p>
        </div>
      ) : (
        <div className="bg-[#161B22] border border-gray-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0D1117] border-b border-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Name</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">VDR Access</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Last Login</th>
                  <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                  <th className="text-right px-4 py-3 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/60">
                {filtered.map((c) => {
                  const busy = rowBusy[c.id];
                  return (
                    <tr key={c.id} className="hover:bg-white/5" data-testid={`contact-row-${c.id}`}>
                      <td className="px-4 py-3 text-white">
                        <button
                          type="button"
                          onClick={() => setViewingContact(c)}
                          className="text-white hover:text-[#00E87A] transition-colors text-left"
                          data-testid={`contact-name-${c.id}`}
                        >
                          {c.name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-gray-500" />
                          {c.email}
                        </span>
                        {c.phone && (
                          <div className="text-xs text-gray-500 mt-0.5 inline-flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> {c.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300">
                          {c.role_label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Toggle
                          checked={c.is_vdr_enabled}
                          disabled={busy === 'toggle'}
                          onChange={(next) => handleToggleVdr(c, next)}
                          testId={`contact-vdr-toggle-${c.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {c.last_login ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-500" />
                            {formatDate(c.last_login)}
                          </span>
                        ) : (
                          <span className="text-gray-500">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {c.is_active ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#00E87A]/20 text-[#00E87A]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          {c.is_vdr_enabled && (
                            <>
                              <button
                                onClick={() => handleSendReminder(c)}
                                disabled={busy === 'reminder'}
                                title="Send reminder"
                                className="p-1.5 rounded hover:bg-amber-500/15 text-amber-400 disabled:opacity-50"
                                data-testid={`contact-reminder-${c.id}`}
                              >
                                {busy === 'reminder' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleResendInvite(c)}
                                disabled={busy === 'resend'}
                                title="Resend invite"
                                className="p-1.5 rounded hover:bg-blue-500/15 text-blue-400 disabled:opacity-50"
                                data-testid={`contact-resend-${c.id}`}
                              >
                                {busy === 'resend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setViewingContact(c)}
                            title="View documents"
                            className="p-1.5 rounded hover:bg-white/10 text-gray-300"
                            data-testid={`contact-view-${c.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingContact(c)}
                            title="Edit"
                            className="p-1.5 rounded hover:bg-white/10 text-gray-300"
                            data-testid={`contact-edit-${c.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingContact(c)}
                            title="Remove"
                            className="p-1.5 rounded hover:bg-red-500/15 text-red-400"
                            data-testid={`contact-delete-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="contacts-toast"
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
            toast.kind === 'error' ? 'bg-red-500/90 text-white' : 'bg-[#00E87A]/95 text-black'
          }`}
        >
          {toast.text}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <ContactFormModal
          mode="add"
          companyId={companyId}
          token={token}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchContacts(); }}
        />
      )}
      {editingContact && (
        <ContactFormModal
          mode="edit"
          initial={editingContact}
          companyId={companyId}
          token={token}
          onClose={() => setEditingContact(null)}
          onSaved={() => { setEditingContact(null); fetchContacts(); }}
        />
      )}
      {deletingContact && (
        <DeleteConfirmModal
          contact={deletingContact}
          companyId={companyId}
          token={token}
          onClose={() => setDeletingContact(null)}
          onDeleted={() => { setDeletingContact(null); fetchContacts(); }}
        />
      )}
      {viewingContact && (
        <PartyDetailPanel
          contact={viewingContact}
          companyId={companyId}
          token={token}
          onClose={() => setViewingContact(null)}
          onOpenDocument={() => { setViewingContact(null); onOpenDocRoom?.(); }}
        />
      )}
    </div>
  );
};

export default AdminContactsTab;
